import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Beaker, X, Filter } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';


const Products = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
    const [isLoading, setIsLoading] = useState(true);

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_URL}/api/categories`);
                const data = await response.json();
                if (data.success && data.data) {
                    setCategories(data.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    // Update selectedCategory and searchQuery when URL changes
    useEffect(() => {
        const categoryFromUrl = searchParams.get('category') || '';
        const searchFromUrl = searchParams.get('search') || '';
        setSelectedCategory(categoryFromUrl);
        setSearchQuery(searchFromUrl);
    }, [searchParams]);

    // Fetch products from API
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                // Fetch from PHP API
                const response = await fetch(`${API_URL}/api/products`);
                const data = await response.json();
                if (data.success) {
                    // Map API response to expected format
                    const mappedProducts = data.data.map(p => {
                        // Handle image URL - use placeholder if no image
                        let imageUrl = p.image;

                        // If no image or empty string, use placeholder
                        if (!imageUrl || imageUrl.trim() === '') {
                            imageUrl = 'https://images.unsplash.com/photo-1603126857599-f6e157fa2fe6?w=800&h=600&fit=crop';
                        }
                        // If it's an API path (with /api prefix), prepend base URL
                        else if (imageUrl.startsWith('/api/uploads')) {
                            imageUrl = `${API_URL}${imageUrl}`;
                        }
                        // If it's an old-style path (without /api prefix), add /api and prepend base URL
                        else if (imageUrl.startsWith('/uploads')) {
                            imageUrl = `${API_URL}/api${imageUrl}`;
                        }

                        return {
                            id: p.id.toString(),
                            productName: p.product_name,
                            synonyms: p.synonyms || '',
                            casNumber: p.cas_number || '',
                            einecs: p.einecs || '',
                            image: imageUrl,
                            category: p.category || '',
                            category_id: p.category_id ? p.category_id.toString() : '',
                            extraSections: (() => {
                                try {
                                    const parsed = JSON.parse(p.extra_data);
                                    if (Array.isArray(parsed) && parsed.length > 0) {
                                        return parsed;
                                    }
                                } catch (e) {
                                    // Not JSON
                                }
                                return [{
                                    title: p.extra_data_title || '',
                                    content: p.extra_data || ''
                                }];
                            })()
                        };
                    });
                    setProducts(mappedProducts);
                }
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProducts();
    }, []);

    // Handle category selection
    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        if (categoryId) {
            setSearchParams({ category: categoryId });
        } else {
            setSearchParams({});
        }
    };

    // Get selected category name
    const getSelectedCategoryName = () => {
        if (!selectedCategory) return null;
        const cat = categories.find(c => c.id.toString() === selectedCategory.toString());
        return cat ? cat.name : null;
    };

    // Filter products based on search query AND category
    const filteredProducts = products.filter(product => {
        // Search filter
        const matchesSearch =
            product.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.synonyms?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.casNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.einecs?.toLowerCase().includes(searchQuery.toLowerCase());

        // Category filter - match by ID or name
        const matchesCategory = !selectedCategory ||
            product.category_id === selectedCategory.toString() ||
            product.category?.toLowerCase() === selectedCategory.toLowerCase() ||
            product.category?.toLowerCase() === getSelectedCategoryName()?.toLowerCase();

        return matchesSearch && matchesCategory;
    });

    return (
        <div className="bg-white min-h-screen">

            {/* Search Bar */}
            <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search by product name, CAS number, or synonyms..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                            />
                        </div>
                        <button className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:shadow-lg transition-all flex items-center justify-center space-x-2">
                            <Search className="h-5 w-5" />
                            <span>Search</span>
                        </button>
                    </div>

                    {/* Category Filter Dropdown */}
                    <div className="mt-6">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-gray-600">
                                <Filter className="h-4 w-4" />
                                <span className="text-sm font-medium">Filter:</span>
                            </div>

                            {/* Category Dropdown */}
                            <div className="relative">
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors cursor-pointer min-w-[200px]"
                                >
                                    <option value="">All Products</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id.toString()}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                                {/* Dropdown Arrow */}
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>

                            {/* Clear Filter Button - Only shown when a category is selected */}
                            {selectedCategory && (
                                <button
                                    onClick={() => handleCategoryChange('')}
                                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                    <span>Clear</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Grid */}
            <section className="py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mb-12"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 font-['Outfit']">
                            Our Products
                        </h2>
                        <p className="text-gray-600">Showing {filteredProducts.length} products</p>
                    </motion.div>

                    {/* Product Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredProducts.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.05 }}
                                className="group"
                            >
                                {/* Product Card */}
                                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden h-full flex flex-col border border-gray-100">
                                    {/* Image Section */}
                                    <div className="relative h-52 overflow-hidden bg-gray-50 flex items-center justify-center p-4">
                                        <img
                                            src={product.image}
                                            alt={product.productName}
                                            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                            onError={(e) => {
                                                e.target.onerror = null; // Prevent infinite loop
                                                e.target.src = 'https://images.unsplash.com/photo-1603126857599-f6e157fa2fe6?w=800&h=600&fit=crop';
                                            }}
                                        />
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-6 flex-1 flex flex-col">
                                        {/* Product Name */}
                                        <h3 className="text-xl font-bold text-gray-900 mb-4 font-['Outfit'] line-clamp-2">
                                            {product.productName}
                                        </h3>

                                        {/* Product Details */}
                                        <div className="space-y-3 mb-6 flex-1">
                                            {/* Synonyms */}
                                            <div className="flex flex-col">
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Synonyms</span>
                                                <span className="text-sm text-gray-700 line-clamp-2">{product.synonyms}</span>
                                            </div>

                                            {/* CAS Number */}
                                            <div className="flex flex-col">
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">CAS Number</span>
                                                <span className="text-sm text-gray-900 font-mono font-medium">{product.casNumber}</span>
                                            </div>

                                            {/* Einecs */}
                                            {product.einecs && (
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Einecs</span>
                                                    <span className="text-sm text-gray-900 font-mono font-medium">{product.einecs}</span>
                                                </div>
                                            )}

                                            {/* Extra Data - show first section */}
                                            {product.extraSections && product.extraSections.length > 0 && (
                                                (product.extraSections[0].title || product.extraSections[0].content) && (
                                                    <div className="flex flex-col mt-2">
                                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                            {product.extraSections[0].title || 'Extra Data'}
                                                        </span>
                                                        {product.extraSections[0].content && (
                                                            <span className="text-sm text-gray-900 font-medium line-clamp-1 mt-1">
                                                                {product.extraSections[0].content}
                                                            </span>
                                                        )}
                                                    </div>
                                                )
                                            )}
                                        </div>

                                        {/* Enquire Now Button */}
                                        <Link
                                            to={`/product/${product.id}`}
                                            className="w-full bg-[#1e3a5f] hover:bg-[#152a45] text-white font-semibold py-3.5 px-6 rounded-xl text-center transition-all duration-300 hover:shadow-lg"
                                        >
                                            Enquire Now
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* No Results Message */}
                    {filteredProducts.length === 0 && (
                        <div className="text-center py-16">
                            <Beaker className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">No products found</h3>
                            <p className="text-gray-500">Try adjusting your search terms</p>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-600 to-primary-700">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl font-bold text-white mb-4 font-['Outfit']">
                            Can't Find What You Need?
                        </h2>
                        <p className="text-xl text-primary-100 mb-8">
                            Contact our team for custom inquiries or special requests
                        </p>
                        <Link
                            to="/contact"
                            className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-full hover:shadow-xl hover:scale-105 transition-all inline-block"
                        >
                            Contact Us
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Products;

